<script setup lang="ts">
import { onMounted, ref } from "vue";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useApi } from "@/services/api";
import { useAuthStore } from "@/stores/auth";
import type { OnboardingStepEmits } from "@/views/onboarding/types";

const emit = defineEmits<OnboardingStepEmits>();

const api = useApi();
const auth = useAuthStore();

const isworkspacePublicIdAvailable = ref(true);
const workspacePublicId = ref<string>("");
const isLoading = ref(false);
const suggestedworkspacePublicId = ref<string | null>(null);

async function getSuggestedworkspacePublicId() {
  const user = auth.currentUser;

  const naiveSuggestedworkspacePublicId =
    user?.first_name && user.first_name.length > 0 && user.first_name.toLowerCase() !== "none"
      ? `${user.first_name.replace(" ", "-")}-workspace`
      : (user?.email.split("@")[0] ?? "");

  const { available, suggested_slug } = await api.checkworkspacePublicIdAvailability(
    naiveSuggestedworkspacePublicId
  );
  isworkspacePublicIdAvailable.value = available;
  workspacePublicId.value = suggested_slug ?? naiveSuggestedworkspacePublicId;
}

async function createWorkspace() {
  isLoading.value = true;
  const userId = auth.currentUser?.id ?? "";
  if (userId.length > 0) {
    const workspaceId = await api.createWorkspace(workspacePublicId.value, userId);
    if (workspaceId) {
      emit("continue", { workspaceId });
    }
  }
  isLoading.value = false;
}

async function checkworkspacePublicIdAvailability() {
  const { available, suggested_slug } = await api.checkworkspacePublicIdAvailability(
    workspacePublicId.value
  );
  isworkspacePublicIdAvailable.value = available;
  suggestedworkspacePublicId.value = suggested_slug;
}

onMounted(async () => {
  await getSuggestedworkspacePublicId();
});
</script>

<template>
  <div class="onboarding-create-workspace-step">
    <div class="form">
      <TextInput
        placeholder="Enter your workspace name"
        label="Workspace name"
        v-model="workspacePublicId"
        @keyup.enter="createWorkspace"
        @keyup="checkworkspacePublicIdAvailability"
      />

      <Transition name="fade">
        <div v-if="!isworkspacePublicIdAvailable && suggestedworkspacePublicId" class="error">
          This workspace name is not unique, try something else. (like
          <a
            href="#"
            @click="
              workspacePublicId = suggestedworkspacePublicId;
              isworkspacePublicIdAvailable = true;
            "
          >
            {{ suggestedworkspacePublicId }}
          </a>
          )
        </div>
      </Transition>
    </div>

    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="loader">
        <SkoreSpinner />
      </div>

      <div v-else class="actions">
        <SimpleButton label="Continue" @click="createWorkspace" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.onboarding-create-workspace-step {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-32);

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-4);

    .error {
      color: var(--content-negative);
    }

    a {
      color: inherit;
    }
  }

  .loader {
    display: flex;
    height: var(--numbers-40);
    align-items: center;
    justify-content: center;

    .skore-spinner {
      height: 100%;
    }
  }

  .actions {
    display: flex;
    width: 100%;

    .skore-button {
      flex: 1;
    }
  }
}
</style>
